import { useCallback, useEffect, useState } from 'react';
import { getDexfProducts } from '../api/dexf';
import { fetchIcfData } from '../api/ml';
import { getConfigurationById } from '../api/vpc';
import { getEmptySolution, getEmptySolutionCornerGuide } from '../components/ProductList/utils';
import { mapToGlobalIfMissing } from '../components/Products/util';
import { useKompis } from '../contexts/kompis';
import { useAppDispatch } from '../store';
import { updateGlobalArticles } from '../store/icf';
import { SINGLE_WALL } from '../utils';
import { filterTypes } from '../utils/pax/filterTypes';
import { useDebounce } from './useDebounce';
const DEBOUNCE_TIMEOUT = 300;
/**
 * If the design has any missing articles, then we look them up in dexf to figure out what is is that we are missing.
 * This is done because unsellable articles are not part of the product fetch we do on initialize.
 */
const getMissingArticlesFromDexf = async (articles, products) => {
    // Get missing articles for the design, if any. Usually these are out of stock or not saleable
    const missingArticles = articles.filter(art => !products.some(p => p.id === art.id));
    // Fetch corresponding products from dexf, might be invalid (not saleable) so don't use them anywhere else.
    // We use them just for filtering out stuff
    const missingDexfProds = missingArticles.length ? await getDexfProducts(missingArticles) : [];
    return missingDexfProds;
};
/**
 * Filter out articles of certain types we should not display.
 */
const filterArticleByType = (article, products, missingDexfProds, filterOnlyMissing) => {
    const product = products.find(p => p.id === article.id);
    if (!product) {
        // If we don't find the product, try the one from dexf instead.
        const productFromDexf = missingDexfProds.find(dp => dp.itemId === `${article.type}-${article.id}`);
        return !filterTypes(productFromDexf?.content?.mainTypeName);
    }
    // In some cases, we only want to filter out articles that are missing from the design.
    return filterOnlyMissing ? true : !filterTypes(product?.product.content?.mainTypeName);
};
/**
 * Provides debounced data fetching functions for icf data and to update the global item lists.
 */
export const useDebouncedDataFetching = () => {
    const dispatch = useAppDispatch();
    const { dexfAppSettings } = useKompis();
    const [singleWallPromise, setSingleWallPromise] = useState(null);
    const [cornerWallPromise, setCornerWallPromise] = useState(null);
    const [isDebouncing, setIsDebouncing] = useState(false);
    const abortPromises = () => {
        singleWallPromise?.abort();
        cornerWallPromise?.abort();
    };
    const { debouncedCallback: fetchSingleWallData, isDebouncing: isDebouncingSingle } = useDebounce(useCallback((params) => {
        const p = dispatch(fetchIcfData({ ...params, serviceSettings: dexfAppSettings }));
        setSingleWallPromise(p);
    }, [dispatch, dexfAppSettings]), DEBOUNCE_TIMEOUT);
    const { debouncedCallback: fetchCornerWallData, isDebouncing: isDebouncingCorner } = useDebounce(useCallback((params) => {
        const p = dispatch(fetchIcfData({ ...params, serviceSettings: dexfAppSettings }));
        setCornerWallPromise(p);
    }, [dispatch, dexfAppSettings]), DEBOUNCE_TIMEOUT);
    const { debouncedCallback: updateGlobalItemLists, isDebouncing: isGlobalItemsDebouncing } = useDebounce(useCallback((products, form, orderedDesignList) => {
        const updateGlobalItemLists = async (products, form, orderedDesignList) => {
            // Check that all articles are global, otherwise convert them
            const globalItemLists = await Promise.all(orderedDesignList.map(async (item) => {
                const articles = (await mapToGlobalIfMissing(item, products)) ?? [];
                // Fetch corresponding products from dexf, might be invalid (not saleable) so don't use them anywhere else.
                const missingDexfProds = await getMissingArticlesFromDexf(articles, products);
                // Filter out unwanted articles if not on B version
                const filteredArticles = articles.filter(art => filterArticleByType(art, products, missingDexfProds));
                return { ...item, itemList: filteredArticles };
            }));
            const emptySolutionCode = form.wall === SINGLE_WALL
                ? getEmptySolution(form)
                : getEmptySolutionCornerGuide(form);
            const emptySolution = await getConfigurationById(emptySolutionCode, dexfAppSettings);
            const emptySolutionItemList = {
                vpc: emptySolutionCode,
                itemList: emptySolution?.itemList ?? []
            };
            // Add the currently displayed empty solution to the item lists as well
            dispatch(updateGlobalArticles([...globalItemLists, emptySolutionItemList]));
        };
        updateGlobalItemLists(products, form, orderedDesignList);
    }, [dispatch, dexfAppSettings]), DEBOUNCE_TIMEOUT);
    useEffect(() => {
        setIsDebouncing(isDebouncingSingle || isDebouncingCorner || isGlobalItemsDebouncing);
    }, [isDebouncingCorner, isDebouncingSingle, isGlobalItemsDebouncing]);
    return {
        /** Fetch data for single wall configuration */
        fetchSingleWallData,
        /** Fetch data for corner wall configuration */
        fetchCornerWallData,
        /** Update item lists with globally available items */
        updateGlobalItemLists,
        /** Boolean for checking if any function is currently in a debounce state */
        isDebouncing,
        /** Function for aborting all pending data fetching requests from the insights API */
        abortPromises
    };
};
//# sourceMappingURL=useDebouncedDataFetching.js.map